import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, apiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { relativeTime } from "../lib/format";
import { MESHCORE_SNR_COLOR_HEX } from "../lib/definitions/protocols";
import { navigate } from "../lib/router";
import { useIsPhone } from "../lib/useIsPhone";
import { TraceRouteMap } from "../components/TraceRouteMap";
import { PacketsViewSwitch, packetsViewHash } from "../components/PacketsViewSwitch";
import {
  DEFAULT_WINDOW,
  NO_MATCH_TEXT,
  NO_SEARCH_TEXT,
  SEARCH_HELP,
  SEARCH_PLACEHOLDER,
  NO_TRACES_TEXT,
  PICK_TEXT,
  TRACES_DESC,
  TRACE_FILTERS,
  TRACE_WINDOWS,
  LINK_HELP,
  MAP_EMPTY_TEXT,
  MAP_TITLE,
  LINK_NEEDS_NODES,
  LINK_TITLE,
  absoluteTime,
  chartPoints,
  chartRange,
  chartY,
  endName,
  leftOutText,
  linkHistoryPath,
  linkRef,
  mapPlan,
  omittedNote,
  seriesLine,
  windowWords,
  appendPage,
  cardLine,
  chipTop,
  detailPath,
  errorText,
  evidenceNote,
  filterCount,
  hashWidthText,
  heardByText,
  hopNote,
  hopStateWord,
  kindLabel,
  linkText,
  linkTitle,
  listPath,
  openablePacket,
  packetLinks,
  progressText,
  routeNote,
  scopeText,
  snrColorName,
  snrKey,
  sourceText,
  tracesHash,
  varies,
  type ChartBox,
  type DetailHop,
  type LinkHistory,
  type LinkRef,
  type LinkSeries,
  type MapHop,
  type SnrColor,
  type TraceDetail,
  type TraceFilter,
  type TraceHop,
  type TraceItem,
  type TraceList,
  type TraceNode,
  type TracePacketRow,
  type TraceWindow,
} from "../lib/traces";
import type { Packet } from "../api/types";

// Packets > Traces. Everything the page says is decided by lib/traces.ts; this file lays it out. The list is one card per
// trace tag, newest first; picking one opens every packet we heard with that tag, each with its route resolved to nodes and
// the signal heard on every hop. A trace is a path the packet is asked to follow, so the hops come from the packet's own
// list, and the SNR under each hop is what that repeater measured when it heard the packet.

function candidateLabel(n: TraceNode): string {
  return n.name || n.id.slice(0, 8);
}

function NodeLink({ node, children }: { node: { id: string }; children: string }) {
  return (
    <a href={`#/nodes/${encodeURIComponent(node.id)}`} className="node-name-link" onClick={(e) => e.stopPropagation()}>
      {children}
    </a>
  );
}

// The arrow into a hop, with the signal that hop's repeater heard under it (the dot is the newest reading's colour).
function Link({ h, onPick, active }: { h: TraceHop; onPick?: () => void; active?: boolean }) {
  const heard = h.snr_n > 0 && h.snr_db !== null;
  const Tag = onPick ? "button" : "span";
  return (
    <Tag
      className={`tr-link-seg${onPick ? " tr-link-btn" : ""}${active ? " tr-link-active" : ""}`}
      title={onPick ? `${linkTitle(h)} Click for this link over time.` : linkTitle(h)}
      {...(onPick ? { type: "button" as const, onClick: onPick, "aria-pressed": !!active } : {})}
    >
      <span className="tr-arrow" aria-hidden="true">→</span>
      <span className={`tr-link-snr${heard ? "" : " tr-link-none"}`}>
        {heard && h.snr_color && (
          <span className="tr-snr-dot" style={{ background: MESHCORE_SNR_COLOR_HEX[h.snr_color as SnrColor] }} aria-label={snrColorName(h.snr_color)} />
        )}
        {linkText(h)}
      </span>
    </Tag>
  );
}

// One repeater: its name with the short hash under it when one node, otherwise the hash with why it has no name. An unclear
// hop in the detail opens the nodes it could be.
function Chip({ h, hashSize, cands, plain }: { h: TraceHop; hashSize: number; cands?: TraceNode[]; plain?: boolean }) {
  const one = h.node !== null;
  const note = hopNote(h);
  const state = hopStateWord(h.kind, hashSize);
  const name = chipTop(h);
  return (
    <span className={`tr-hop tr-hop-${h.kind}`}>
      {one && h.node ? (
        <>
          <span className="tr-hop-id" title={name}>{plain ? name : <NodeLink node={h.node}>{name}</NodeLink>}</span>
          <span className="tr-hop-hex tr-hex">{h.hash}</span>
        </>
      ) : cands && cands.length > 0 ? (
        <details className="tr-cands">
          <summary title={note}>
            <span className="tr-hop-id tr-hex">{h.hash}</span>
            <span className="tr-hop-state">{state}</span>
          </summary>
          <div className="tr-cands-box">
            <div className="tr-cands-head">{note}</div>
            <ul>
              {cands.map((c) => (
                <li key={c.id}>
                  <NodeLink node={c}>{candidateLabel(c)}</NodeLink>
                  {c.last_heard && <span className="tr-cand-meta"> heard {relativeTime(c.last_heard)}</span>}
                </li>
              ))}
              {h.n_candidates > cands.length && <li className="tr-cand-meta">and {h.n_candidates - cands.length} more</li>}
            </ul>
          </div>
        </details>
      ) : (
        <span title={note}>
          <span className="tr-hop-id tr-hex">{h.hash}</span>
          <span className="tr-hop-state">{state}</span>
        </span>
      )}
    </span>
  );
}

// A route as the packet travels it: the sender (the packet does not say who it is), then a link and a repeater for each hop.
function Chain({ hops, hashSize, label, cands, plain, onLink, activeLink }: { hops: TraceHop[]; hashSize: number; label: string; cands?: TraceNode[][]; plain?: boolean; onLink?: (r: LinkRef) => void; activeLink?: LinkRef | null }) {
  return (
    <div className={`tr-chain${plain ? " tr-preview" : ""}`} role="list" aria-label={label}>
      <span className="tr-sender" role="listitem" title="The packet does not say who sent it.">Sender</span>
      {hops.map((h, i) => (
        <span key={i} className="tr-step" role="listitem">
          {(() => {
            const ref = onLink ? linkRef(hops, i) : null;
            const on = ref !== null && activeLink?.from === ref.from && activeLink?.to === ref.to;
            return <Link h={h} onPick={ref && onLink ? () => onLink(ref) : undefined} active={on} />;
          })()}
          <Chip h={h} hashSize={hashSize} cands={cands?.[i]} plain={plain} />
        </span>
      ))}
    </div>
  );
}

function KindBadge({ kind }: { kind: TraceItem["kind"] }) {
  return <span className={`tr-badge tr-badge-${kind === "TRACE" ? "trace" : "ping"}`}>{kindLabel(kind)}</span>;
}

function Card({ item, active, onPick }: { item: TraceItem; active: boolean; onPick: () => void }) {
  return (
    <button type="button" className={`tr-card${active ? " tr-card-active" : ""}`} aria-pressed={active} onClick={onPick}>
      <span className="tr-card-head">
        <span className="tr-tag tr-hex">{item.tag}</span>
        <KindBadge kind={item.kind} />
        <span className="tr-card-time" title={absoluteTime(item.last_heard)}>{relativeTime(item.last_heard)}</span>
      </span>
      <span className="tr-card-line">{cardLine(item)}</span>
      <Chain hops={item.hops} hashSize={item.hash_size} label="Route with the signal on each link" plain />
    </button>
  );
}

const CHART: ChartBox = { w: 300, h: 150, left: 34, right: 8, top: 8, bottom: 8 };

// One direction as a small time chart: a dot per reading (coloured like the dots elsewhere), a dashed line at the median.
function SeriesChart({ title, s, lo, hi, since, window }: { title: string; s: LinkSeries; lo: number; hi: number; since: string; window: TraceWindow }) {
  const t0 = Date.parse(since);
  const t1 = Date.now();
  const pts = chartPoints(s.readings, CHART, t0, t1, lo, hi);
  const [from, to] = windowWords(window);
  return (
    <div className="tr-series">
      <div className="tr-series-title">{title}</div>
      <svg viewBox={`0 0 ${CHART.w} ${CHART.h}`} className="tr-chart" role="img" aria-label={`${title}. ${seriesLine(s)}`}>
        <line x1={CHART.left} x2={CHART.w - CHART.right} y1={chartY(lo, CHART, lo, hi)} y2={chartY(lo, CHART, lo, hi)} className="tr-chart-axis" />
        <line x1={CHART.left} x2={CHART.left} y1={CHART.top} y2={CHART.h - CHART.bottom} className="tr-chart-axis" />
        <text x={2} y={CHART.top + 4} className="tr-chart-label">dB</text>
        <text x={CHART.left - 4} y={CHART.top + 4} className="tr-chart-label" textAnchor="end">{hi}</text>
        <text x={CHART.left - 4} y={CHART.h - CHART.bottom} className="tr-chart-label" textAnchor="end">{lo}</text>
        {s.median !== null && (
          <line x1={CHART.left} x2={CHART.w - CHART.right} y1={chartY(s.median, CHART, lo, hi)} y2={chartY(s.median, CHART, lo, hi)} className="tr-chart-median" />
        )}
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill={p.color ? MESHCORE_SNR_COLOR_HEX[p.color] : "currentColor"} className="tr-chart-dot">
            <title>{`${p.snr} dB, ${absoluteTime(p.t)}`}</title>
          </circle>
        ))}
      </svg>
      <div className="tr-chart-x"><span>{from}</span><span>{to}</span></div>
      <p className="node-detail-note tr-series-line">{seriesLine(s)}</p>
      {s.left_out > 0 && <p className="node-detail-note tr-series-line">{leftOutText(s.left_out)}</p>}
    </div>
  );
}

function LinkPanel({ link, window, onClose }: { link: LinkRef; window: TraceWindow; onClose: () => void }) {
  const [data, setData] = useState<LinkHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const ctl = new AbortController();
    setData(null);
    setError(null);
    apiGet<LinkHistory>(linkHistoryPath(link, window), ctl.signal)
      .then(setData)
      .catch((e) => !ctl.signal.aborted && setError(errorText(e instanceof ApiError ? e.status : undefined)));
    return () => ctl.abort();
  }, [link.from, link.to, window]);
  const range = data ? chartRange([data.forward, data.reverse]) : { lo: -10, hi: 10 };
  return (
    <div className="tr-linkpanel" role="region" aria-label={LINK_TITLE}>
      <div className="tr-linkpanel-head">
        <span className="k decode-heading">{LINK_TITLE}</span>
        <button type="button" className="sign-out-btn" onClick={onClose}>Close</button>
      </div>
      <p className="node-detail-note tr-meta">{LINK_HELP}</p>
      {error && <div className="decode-error" role="alert">{error}</div>}
      {!error && !data && <div className="node-detail-note">Loading…</div>}
      {data && (
        <div className="tr-series-row">
          <SeriesChart title={`${endName(data.from)} → ${endName(data.to)}`} s={data.forward} lo={range.lo} hi={range.hi} since={data.since} window={window} />
          {data.reverse && <SeriesChart title={`${endName(data.to)} → ${endName(data.from)}`} s={data.reverse} lo={range.lo} hi={range.hi} since={data.since} window={window} />}
        </div>
      )}
    </div>
  );
}

function RouteMap({ route }: { route: MapHop[] }) {
  const { effectiveTheme } = useAppData();
  const plan = useMemo(() => mapPlan(route), [route]);
  return (
    <div className="tr-mapbox">
      <div className="k decode-heading">{MAP_TITLE}</div>
      {plan.points.length === 0 ? (
        <p className="node-detail-note">{MAP_EMPTY_TEXT}</p>
      ) : (
        <TraceRouteMap key={effectiveTheme} plan={plan} theme={effectiveTheme} height={280} />
      )}
      {omittedNote(plan.omitted) && <p className="node-detail-note tr-meta">{omittedNote(plan.omitted)}</p>}
    </div>
  );
}

function PacketCard({ p, showRouteType, showScope, onOpen }: { p: TracePacketRow; showRouteType: boolean; showScope: boolean; onOpen: (row: Record<string, unknown>) => void }) {
  const row = openablePacket(p);
  return (
    <div className="tr-packet">
      <div className="tr-packet-head">
        <span className="tr-packet-src">{sourceText(p)}</span>
        {showRouteType && <span>{p.route_type}</span>}
        {showScope && <span>{scopeText(p.scope)}</span>}
        <span className="tr-packet-times" title={`First heard ${absoluteTime(p.first_heard)}, last ${absoluteTime(p.last_heard)}`}>
          {p.first_heard === p.last_heard ? `Heard ${relativeTime(p.first_heard)}` : `First heard ${relativeTime(p.first_heard)}, last ${relativeTime(p.last_heard)}`}
        </span>
      </div>
      <Chain hops={packetLinks(p.hops)} hashSize={p.hash_size} label="This packet's route with the signal on each link" cands={p.hops.map((h) => h.candidates)} />
      <div className="tr-packet-foot">
        <span className="node-detail-note">
          {progressText(p.reached, p.hops.length)}. {heardByText(p)}
        </span>
        {row && (
          <button type="button" className="sign-out-btn tr-open" onClick={() => onOpen(row)}>
            Open packet
          </button>
        )}
      </div>
    </div>
  );
}

function Detail({ tag, window, onBack, showBack }: { tag: string; window: TraceWindow; onBack: () => void; showBack: boolean }) {
  const { select } = useAppData();
  const [detail, setDetail] = useState<TraceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkSel, setLinkSel] = useState<LinkRef | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    setLinkSel(null);
    setDetail(null);
    setError(null);
    apiGet<TraceDetail>(detailPath(tag, window), ctl.signal)
      .then(setDetail)
      .catch((e) => {
        if (ctl.signal.aborted) return;
        setError(e instanceof ApiError && e.status === 404 ? "No trace with this tag was heard in this window." : errorText(e instanceof ApiError ? e.status : undefined));
      });
    return () => ctl.abort();
  }, [tag, window]);

  return (
    <section className="tr-detail" aria-label="Trace detail">
      {showBack && (
        <button type="button" className="sign-out-btn tr-back" onClick={onBack}>
          Back to the list
        </button>
      )}
      <div className="tr-detail-head">
        <span className="tr-tag tr-hex">{tag}</span>
        {detail && <KindBadge kind={detail.kind} />}
        {detail && (
          <span className="node-detail-note">
            {detail.packets.length === 1 ? "1 packet" : `${detail.packets.length} packets`}, first heard {relativeTime(detail.first_heard)}, last {relativeTime(detail.last_heard)}
          </span>
        )}
      </div>
      {error && <div className="decode-error" role="alert">{error}</div>}
      {!error && detail === null && <div className="node-detail-note">Loading…</div>}
      {detail && (
        <>
          <p className="node-detail-note tr-meta">
            {hashWidthText(detail.hash_size)}.{showBack ? ` ${snrKey(detail.snr_policy)}` : ""}
          </p>
          <div className="k decode-heading">Route</div>
          <p className="node-detail-note tr-meta">{routeNote(detail.route_packets)}</p>
          <Chain
            hops={detail.route as unknown as TraceHop[]}
            hashSize={detail.hash_size}
            label="Route combined from every packet, with the signal on each link"
            cands={detail.route.map((h) => h.candidates)}
            onLink={setLinkSel}
            activeLink={linkSel}
          />
          <p className="node-detail-note tr-meta">{LINK_NEEDS_NODES} Click a link's arrow.</p>
          {linkSel && <LinkPanel link={linkSel} window={window} onClose={() => setLinkSel(null)} />}
          <RouteMap route={detail.route as unknown as MapHop[]} />
          {evidenceNote(detail.path_context, detail.route) && <p className="node-detail-note">{evidenceNote(detail.path_context, detail.route)}</p>}
          <div className="k decode-heading">Packets, oldest first</div>
          {detail.packets.map((p) => (
            <PacketCard
              key={p.id}
              p={p}
              showRouteType={varies(detail.packets.map((x) => x.route_type))}
              showScope={varies(detail.packets.map((x) => x.scope))}
              onOpen={(row) => select({ kind: "packet", packet: row as unknown as Packet })}
            />
          ))}
        </>
      )}
    </section>
  );
}

export function TracesPage({ presetTag, presetWindow }: { presetTag: string | null; presetWindow: TraceWindow | null }) {
  const isPhone = useIsPhone();
  const [filter, setFilter] = useState<TraceFilter>("all");
  const [win, setWin] = useState<TraceWindow>(presetWindow ?? DEFAULT_WINDOW);
  const [list, setList] = useState<TraceList | null>(null);
  const [items, setItems] = useState<TraceItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [tag, setTag] = useState<string | null>(presetTag);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  // The search box feeds the request after a short pause, so each keystroke is not a query.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const gen = useRef(0);

  // A link to a tag (from a packet's own panel) opens it, in the window that reaches it.
  useEffect(() => {
    setTag(presetTag);
    if (presetWindow) setWin(presetWindow);
  }, [presetTag, presetWindow]);

  const load = useCallback(
    (cursor: string | null) => {
      const mine = ++gen.current;
      const ctl = new AbortController();
      if (cursor) setMore(true);
      apiGet<TraceList>(listPath(filter, win, cursor, undefined, query), ctl.signal)
        .then((res) => {
          if (mine !== gen.current) return;
          setError(null);
          setList(res);
          setItems((have) => (cursor ? appendPage(have, res.items) : res.items));
        })
        .catch((e) => {
          if (mine !== gen.current || ctl.signal.aborted) return;
          setError(errorText(e instanceof ApiError ? e.status : undefined));
        })
        .finally(() => mine === gen.current && setMore(false));
      return () => ctl.abort();
    },
    [filter, win, query],
  );

  useEffect(() => {
    setList(null);
    setItems([]);
    return load(null);
  }, [load]);

  function pick(t: string | null) {
    setTag(t);
    navigate(tracesHash(t, win));
  }

  const showDetail = tag !== null;
  const showList = !(isPhone && showDetail);

  return (
    <div className="node-list-page tr-page">
      <h2 className="audit-page-title">Packets</h2>
      <PacketsViewSwitch active="traces" onSelect={(v) => navigate(packetsViewHash(v))} />
      <p className="node-detail-note">{TRACES_DESC}</p>

      {showList && (
        <div className="tr-search">
          <input
            className="node-list-search"
            type="search"
            aria-label="Search traces"
            title={SEARCH_HELP}
            placeholder={SEARCH_PLACEHOLDER}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      {showList && (
        <div className="tr-controls">
          <div className="theme-toggle" role="group" aria-label="Trace type">
            {TRACE_FILTERS.map((f) => (
              <button key={f.id} data-active={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.label}
                {list && <span className="tr-count"> {filterCount(list.counts, f.id)}</span>}
              </button>
            ))}
          </div>
          <div className="theme-toggle" role="group" aria-label="Time window">
            {TRACE_WINDOWS.map((w) => (
              <button key={w.id} data-active={win === w.id} onClick={() => setWin(w.id)}>
                Last {w.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showList && list && <p className="node-detail-note tr-meta">{snrKey(list.snr_policy)}</p>}

      {error && <div className="decode-error" role="alert">{error}</div>}

      <div className={`tr-layout${isPhone ? "" : " tr-layout-split"}`}>
        {showList && (
          <div className="tr-list">
            {list === null && !error && <div className="node-detail-note">Loading…</div>}
            {list !== null && items.length === 0 && <div className="empty-state">{query ? NO_SEARCH_TEXT : list.counts.all === 0 ? NO_TRACES_TEXT : NO_MATCH_TEXT}</div>}
            {items.map((i) => (
              <Card key={i.tag} item={i} active={i.tag === tag} onPick={() => pick(i.tag)} />
            ))}
            {list?.next_cursor && (
              <button type="button" className="sign-out-btn tr-more" disabled={more} onClick={() => load(list.next_cursor)}>
                {more ? "Loading…" : "Show more"}
              </button>
            )}
          </div>
        )}
        {showDetail && tag && <Detail tag={tag} window={win} showBack={isPhone} onBack={() => pick(null)} />}
        {!showDetail && !isPhone && <div className="tr-pick node-detail-note">{PICK_TEXT}</div>}
      </div>
    </div>
  );
}
